/**
 * Skeleton loading components for better perceived performance
 */

const shimmer = "relative overflow-hidden before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_1.5s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/60 before:to-transparent";

/**
 * Base skeleton block
 */
export const Skeleton = ({ className = '' }) => (
  <div className={`bg-site-bg-alt rounded ${shimmer} ${className}`} />
);

/**
 * Product card skeleton - matches ProductGroupCard layout
 */
export const ProductCardSkeleton = () => (
  <div className="product-card block relative bg-white border border-black/10">
    {/* Image placeholder */}
    <div className="aspect-[4/3] bg-site-bg-alt overflow-hidden">
      <Skeleton className="w-full h-full rounded-none" />
    </div>
    
    {/* Info Section */}
    <div className="p-4 pt-2 pb-0 bg-white border-b border-r border-black/10">
      {/* Category */}
      <div className="h-4 overflow-hidden">
        <Skeleton className="h-2.5 w-1/3" />
      </div>
      
      {/* Title */}
      <Skeleton className="h-4 w-3/4 mt-1" />
      
      {/* Description */}
      <div className="h-11 flex flex-col justify-center gap-1.5 mt-1">
        <Skeleton className="h-2.5 w-full" />
        <Skeleton className="h-2.5 w-2/3" />
      </div>
      
      {/* Products List */}
      <div className="pt-1 border-t border-black/5">
        <Skeleton className="h-2 w-24 mb-1.5" />
        <div className="h-7 flex items-center gap-1">
          <Skeleton className="h-5 w-20 rounded-full" />
          <Skeleton className="h-5 w-16 rounded-full" />
          <Skeleton className="h-5 w-14 rounded-full" />
        </div>
      </div>
      
      {/* Shop Button */}
      <Skeleton className="mt-2 h-10 w-full rounded-none" />
      
      {/* Tags */}
      <div className="h-10 flex items-center gap-1.5">
        <Skeleton className="h-6 w-12 rounded" />
        <Skeleton className="h-6 w-16 rounded" />
      </div>
    </div>
  </div>
);

/**
 * Product grid skeleton - matches homepage/category page layouts
 */
export const ProductGridSkeleton = ({ count = 4 }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0">
    {Array.from({ length: count }).map((_, i) => (
      <ProductCardSkeleton key={i} />
    ))}
  </div>
);

/**
 * Editor's pick row skeleton
 */
export const EditorPickRowSkeleton = () => (
  <div className="flex gap-6 py-8 border-b border-muted">
    <div className="w-1/2">
      <Skeleton className="aspect-[4/5] rounded-xl" />
    </div>
    <div className="w-1/2 space-y-4">
      <Skeleton className="h-8 w-3/4" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-2/3" />
      <div className="flex gap-3 mt-6">
        <Skeleton className="h-10 w-32" />
        <Skeleton className="h-10 w-32" />
      </div>
    </div>
  </div>
);

/**
 * Category card skeleton
 */
export const CategoryCardSkeleton = () => (
  <div className="bg-white border border-muted rounded-xl overflow-hidden">
    <Skeleton className="aspect-square" />
    <div className="p-4">
      <Skeleton className="h-5 w-2/3" />
    </div>
  </div>
);

/**
 * Table row skeleton
 */
export const TableRowSkeleton = ({ columns = 4 }) => (
  <tr className="border-b border-muted">
    {Array.from({ length: columns }).map((_, i) => (
      <td key={i} className="p-4">
        <Skeleton className="h-4 w-full" />
      </td>
    ))}
  </tr>
);

/**
 * Admin list skeleton
 */
export const AdminListSkeleton = ({ rows = 5 }) => (
  <div className="space-y-3">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="bg-white border border-muted rounded-lg p-4 flex items-center gap-4">
        <Skeleton className="w-12 h-12 rounded-full flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-3 w-1/4" />
        </div>
        <Skeleton className="h-8 w-20" />
      </div>
    ))}
  </div>
);

/**
 * Text skeleton (paragraph)
 */
export const TextSkeleton = ({ lines = 3 }) => (
  <div className="space-y-2">
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton 
        key={i} 
        className={`h-4 ${i === lines - 1 ? 'w-2/3' : 'w-full'}`} 
      />
    ))}
  </div>
);

/**
 * Hero section skeleton
 */
export const HeroSkeleton = () => (
  <div className="py-16 px-6 text-center space-y-4">
    <Skeleton className="h-4 w-24 mx-auto" />
    <Skeleton className="h-12 w-2/3 mx-auto" />
    <Skeleton className="h-6 w-1/2 mx-auto" />
    <div className="flex justify-center gap-4 mt-8">
      <Skeleton className="h-12 w-40" />
      <Skeleton className="h-12 w-40" />
    </div>
  </div>
);

/**
 * Profile page skeleton
 */
export const ProfileSkeleton = () => (
  <div className="max-w-2xl mx-auto p-6 space-y-6">
    <div className="flex items-center gap-4">
      <Skeleton className="w-20 h-20 rounded-full" />
      <div className="space-y-2">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-4 w-56" />
      </div>
    </div>
    <div className="space-y-4">
      <Skeleton className="h-20 w-full rounded-lg" />
      <Skeleton className="h-20 w-full rounded-lg" />
      <Skeleton className="h-20 w-full rounded-lg" />
    </div>
  </div>
);

export default Skeleton;
